/**
 * Feedback Details Step
 *
 * Description input and file upload for feedback.
 */

import { File, Trash2, Upload } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { Button, Card, Label, Textarea } from '@tonnie/ui';
import { useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { formatFileSize, type IFeedbackData, type IFeedbackFile, MAX_FILE_SIZE } from './constants';

interface IFeedbackDetailsStepProps {
  feedback: IFeedbackData;
  onDescriptionChange: (description: string) => void;
  onFilesChange: (files: IFeedbackFile[]) => void;
  isSubmitting: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

export function FeedbackDetailsStep({
  feedback,
  onDescriptionChange,
  onFilesChange,
  isSubmitting,
  textareaRef,
}: IFeedbackDetailsStepProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);
      if (selectedFiles.length === 0) return;

      const oversizedFiles = selectedFiles.filter((file) => file.size > MAX_FILE_SIZE);
      if (oversizedFiles.length > 0) {
        toast.error(t('feedback.fileSizeLimit'));
        return;
      }

      const newFiles: IFeedbackFile[] = selectedFiles.map((file) => {
        const id = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const feedbackFile: IFeedbackFile = {
          id,
          file,
          size: file.size,
          type: file.type,
        };

        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onloadend = () => {
            onFilesChange(
              [...feedback.files, ...newFiles].map((f) =>
                f.id === id ? { ...f, preview: reader.result as string } : f
              )
            );
          };
          reader.readAsDataURL(file);
        }

        return feedbackFile;
      });

      onFilesChange([...feedback.files, ...newFiles]);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [feedback.files, onFilesChange, t]
  );

  const handleRemoveFile = useCallback(
    (fileId: string) => {
      onFilesChange(feedback.files.filter((f) => f.id !== fileId));
    },
    [feedback.files, onFilesChange]
  );

  const totalFileSize = feedback.files.reduce((total, f) => total + f.size, 0);

  return (
    <div className="space-y-6">
      {/* Description */}
      <div>
        <h3 className="font-semibold text-base mb-2">{t('feedback.tellUsMore')}</h3>
        <p className="text-sm text-muted-foreground mb-4">{t('feedback.pleaseDescribe')}</p>
        <Textarea
          data-testid="feedback-input"
          ref={textareaRef}
          value={feedback.description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder={t('widgets.motivationalForm.sharePlaceholder')}
          className="min-h-[180px] resize-none"
          maxLength={2000}
          aria-label={t('feedback.aria.description')}
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-muted-foreground">
            {t('feedback.characters', { current: feedback.description.length, max: 2000 })}
          </p>
        </div>
      </div>

      {/* File Upload */}
      <div>
        <Label className="text-sm font-medium mb-2 block">
          {t('feedback.attachFilesOptional')}
        </Label>
        <input
          data-testid="feedback-file-input"
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          onChange={handleFileSelect}
          className="hidden"
          aria-label={t('feedback.aria.uploadFiles')}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          data-testid="feedback-attach-button"
          className="w-full"
          disabled={isSubmitting}
        >
          <Upload className="w-4 h-4 mr-2" aria-hidden="true" />
          {t('feedback.uploadFiles')}
        </Button>

        {feedback.files.length > 0 && (
          <div className="mt-4 space-y-2">
            {feedback.files.map((file) => (
              <Card key={file.id} className="p-3 bg-muted/30">
                <div className="flex items-start gap-3">
                  {file.preview ? (
                    <div className="w-12 h-12 rounded border border-sidebar-border overflow-hidden shrink-0">
                      <img
                        src={file.preview}
                        alt={file.file.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded border border-sidebar-border flex items-center justify-center bg-muted shrink-0">
                      <File className="w-6 h-6 text-muted-foreground" aria-hidden="true" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.file.name}</p>
                    <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveFile(file.id)}
                    className="h-8 w-8 shrink-0"
                    disabled={isSubmitting}
                    aria-label={t('feedback.aria.removeFile', { name: file.file.name })}
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </Button>
                </div>
              </Card>
            ))}
            <div className="text-xs text-muted-foreground text-right">
              {t('feedback.totalSize', { size: formatFileSize(totalFileSize) })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
